import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

import { Source, Layer } from 'react-map-gl';
import MapGL from 'react-map-gl';
import { Size, useSize } from 'react-hook-size';
import { ViewState } from 'react-map-gl-draw/node_modules/@types/react-map-gl';

import _ from "lodash";

import ESRI_BASE from '../../../../components/Map/styles/esri-base.json';
import ESRI_BASE_SATELLITE from '../../../../components/Map/styles/esri-base-satellite.json';
import { MAPBOX_BASE_SATELLITE } from '../../../../components/Map/styles/mapbox-base-satellite';
import NAIP from '../../../../components/Map/styles/naip.json';

import { editFieldsLayer, fieldsBorderLayer, fieldsHighlightLayer, importFieldsBorderLayer, importFieldsLayer } from '../../../../components/Map/styles/fields';
import { createFeatureCollection, createFeatureFromField, getViewport, INTERNAL_FIELD_ID, zoomToFeatures } from '../../../../utils/mapUtils';

import { Field, LayerVisibility } from '../../../../types/types';
import { useQuery } from 'react-apollo';
import { GET_FIELDS } from '../../../../../../queries';
import { GetFields } from '../../../../../../__generated__/types';

import {NavigationControl} from 'react-map-gl';

import '../../../../styles/map-gl.scss';
import { useParams } from 'react-router-dom';
import { generatePath } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import { Feature, FeatureCollection } from '@turf/turf';
import ImportForm from './ImportForm';
import LayerVisibilityControlPanel from '../LayerVisibilityControlPanel';
import { ImportPanel } from './ImportPanel';
import { Popup } from 'react-map-gl';

import * as turf from '@turf/turf';



const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;

const navControlStyle= {
    left: 10,
    top: 10
};

const initLayerVisibility: LayerVisibility[] = [{
    layer_id: 'naip',
    layer_name: 'Historical Imagery',
    is_visible: true,
    order: 1,
    source: NAIP,
},{
    layer_id: 'mapbox',
    layer_name: 'Mapbox Imagery',
    is_visible: true,
    order: 2,
    source: MAPBOX_BASE_SATELLITE,

}];

export const ImportFields = () => {
    const history = useHistory();

    const mapContainerRef = useRef(null);

    const { vp } = useParams();

    const { width, height } = useSize(mapContainerRef);

    const [viewport, setViewport] = useState<ViewState>(getViewport(vp));

    const [size, setSize] = useState<Size>({
        width: width || 800,
        height: height || 800,
    });

    const [overlayVisibility, setOverlayVisible] = useState<LayerVisibility[]>(initLayerVisibility);
    const [fieldsDataSource, setFieldsDataSource] = useState(null);
    const [featureCollection, setFeatureCollection] = useState<FeatureCollection | null>(null);
    const [hoverInfo, setHoverInfo] = useState<any>(null);
    const [hoverFieldId, setHoverFieldId] = useState<number | null>(null);


    const {data: fieldData} = useQuery<GetFields>(GET_FIELDS);

    useEffect(() => {
        if (width && height) {
            setSize({width, height});
        }
    }, [width, height]);

    useEffect(() => {
        if(fieldData) {
            // convert the fields to a feature collection
            const fields = fieldData.landscan_fields.map((f:any) => {
                return {...f,id: f.field_id} as Field;
            });

            const features = fields.map((f:Field) => createFeatureFromField(f));
            const fieldCollection = createFeatureCollection(features);

            setFieldsDataSource(fieldCollection);
        }
    }, [fieldData]);

    useEffect(() => {
        if(featureCollection && width && height) {
            setViewport(zoomToFeatures(featureCollection, viewport, width, height, 200));
        }
    }, [featureCollection]);

    const delayedUrlUpdate = React.useCallback(_.debounce((url:any) => history.replace(url), 250), []);

    const onInteractionStateChange = (e: any) => {
        if(!e.isDragging && !e.isPanning && !e.isZooming) {
            const str = `@${viewport.latitude.toFixed(5)},${viewport.longitude.toFixed(5)},${viewport.zoom.toFixed(2)}`;
            const url = generatePath(`${FIELD_APP_URL}/:vp/fields/import`, {vp: str});
              
            delayedUrlUpdate(url);
        }
    }

    const getCursor = () => {
        return 'pointer';
    }

    const prepareForImport = (collection: FeatureCollection): FeatureCollection => {
        collection.features.forEach((f: Feature, index: number) => {
            f.id = index+1;
            f.properties![INTERNAL_FIELD_ID] = index+1;
        });
        
        return collection;
    }

    const onHover = React.useCallback(event => {
        const feature = event.features && event.features[0];

        setHoverInfo(feature ? {
            longitude: event.lngLat[0],
            latitude: event.lngLat[1],
            properties: feature && feature.properties
          } : null);
      }, []);

    const onSelectField = React.useCallback((id: number | null) => {

    }, []);  

    const onHoverField = React.useCallback((id: number | null) => {
        setHoverFieldId(id);  
    }, []);

    const onFileImport = (collection: FeatureCollection) => {
        // the geojson that comes from shape to geojson importer 
        // results in 3d coordinates. hasura can't handle that so
        // the z dimension is removed.
        var options = {precision: 6, coordinates: 2};
        var truncated = turf.truncate(collection, options);
        
        setFeatureCollection(prepareForImport(truncated));
    };

    const onFileClear = React.useCallback(() => {
        setFeatureCollection(null);
    }, []);
 
    const hoverId = hoverFieldId || '';
    const hoverfilter = React.useMemo(() => ['in', INTERNAL_FIELD_ID, hoverId], [hoverId]);

    return (
        <div className="d-flex h-100">
            <div className="bg-light border border-bottom-0 border-dark border-top-0 d-flex flex-column flex-shrink-0 shadow-sm text-dark" style={{ width: '450px' }}>
                <ImportForm 
                    features={featureCollection}
                    onSelectField={onSelectField}
                    onHoverField={onHoverField}>       
                </ImportForm>
            </div>
            <div className="flex-fill bg-white position-relative" ref={mapContainerRef}>
                <MapGL
                    {...viewport}
                    width='100%'
                    height='100%'
                    onHover={onHover}
                    getCursor={() => getCursor()}
                    doubleClickZoom={false}
                    mapStyle={ESRI_BASE}
                    interactiveLayerIds={featureCollection ? ['import-field-fill'] : []}
                    onInteractionStateChange={(s:any) => onInteractionStateChange(s)} 
                    onViewportChange={(v:ViewState) => setViewport(v)}> 
                    {
                    overlayVisibility.map((layer: LayerVisibility) => (
                        //console.log(layer),
                        <Source 
                            key={`s-${layer.layer_id}`}
                            id={layer.layer_id}
                            type='raster'
                            tileSize={256}
                            tiles={layer.source.sources['raster-tiles'].tiles}>
                            <Layer 
                                key={layer.layer_id} 
                                id={layer.layer_id} 
                                type='raster' 
                                source={layer.layer_id} 
                                maxzoom={layer.source.layers[0].maxzoom} 
                                minzoom={layer.source.layers[0].minzoom} 
                                layout={{ visibility: layer.is_visible ? 'visible' : 'none' }} 
                            />
                        </Source>
                        ))
                    }

                    <Source type="geojson" data={fieldsDataSource}>
                        <Layer {...editFieldsLayer} />
                        <Layer {...fieldsBorderLayer} />
                    </Source>

                    { featureCollection &&
                        <Source type="geojson" data={featureCollection}>
                            <Layer {...importFieldsLayer} />
                            <Layer {...importFieldsBorderLayer} />
                            <Layer {...fieldsHighlightLayer} filter={hoverfilter} />
                        </Source>
                    }

                    {hoverInfo && (
                        <Popup
                            longitude={hoverInfo.longitude}
                            latitude={hoverInfo.latitude}
                            closeButton={false}
                            className="import-field-info"
                        >
                            <table className="table table-sm table-dark">
                                <thead>
                                    <tr>
                                        <th>Property</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    { Object.keys(hoverInfo.properties).filter((k: string) => k !== INTERNAL_FIELD_ID).map((key: string) => {
                                       return hoverInfo.properties[key] ?
                                        <tr key={key}>
                                            <td>{key}</td>
                                            <td>{hoverInfo.properties[key]}</td>
                                        </tr> : null;
                                    })}
                                </tbody>
                            </table>
                        </Popup>
                    )}
                    
                    <NavigationControl style={navControlStyle} showCompass={false} />
                    <div style={{position: 'absolute', bottom:'10px', left:'10px'}}>
                        <LayerVisibilityControlPanel showDatePicker={false} layers={overlayVisibility} onChange={(visibility: LayerVisibility[]) => setOverlayVisible(visibility)} />
                    </div>                    
                    <ImportPanel onImport={onFileImport} onClear={onFileClear} />
                </MapGL>
            </div>
        </div>
    );
}
